import { usePackageBookingStore } from "@elixir/stores";
import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  MODES_OF_APT,
  PACKAGE_POPUP_TYPE,
  PACKAGE_PRICING_MODEL,
  PACKAGE_STATUS,
  PACKAGE_TYPE,
  SlotTime,
  TInclusionPayload,
  TYPES_OF_APT,
} from "@elixir/types";
import { formattedPackageDetails, getAge } from "@elixir/utils";
import { Drawer, notification } from "antd";
import dayjs from "dayjs";
import React from "react";
import { ModalFooter, DrawerHeaderUi } from "ui";
import usePostPatientPackageStart from "../../../../apis/tryst/packages-and-treatments/use-post-patient-package-start";
import useQueueStore from "../../../../stores/queue-store";
import { track } from "../../../../utils/tracking";
import PackageBreakdown from "../popup-content/package-breakdown";
import SessionDetails from "../popup-content/session-details";
import AvailableSlotsList from "./available-slots-list";
import PackageSelection from "./package-selection";

const StartTreatmentPopup = () => {
  React.useEffect(() => {
    track("start_treatment_popup");
  }, []);

  const packageDetails = usePackageBookingStore(
    (state) => state.packageDetails
  );
  const clearPackageDetails = usePackageBookingStore(
    (state) => state.clearPackageDetails
  );
  const packagePopupType = usePackageBookingStore(
    (state) => state.packagePopupType
  );

  const sessionList = usePackageBookingStore((state) => state.sessionList);
  const setSessionList = usePackageBookingStore(
    (state) => state.setSessionList
  );
  const setSessionFromDateSelector = usePackageBookingStore(
    (state) => state.setSessionFromDateSelector
  );
  const selectedPatient = usePackageBookingStore(
    (state) => state.selectedPatient
  );
  const conductingDoctor = usePackageBookingStore(
    (state) => state.conductingDoctor
  );

  const packageClinicId = usePackageBookingStore(
    (state) => state.selectedClinicId
  );
  const queueClinicId = useQueueStore((state) => state.clinicId);
  const selectedClinicId = packageClinicId || queueClinicId;

  const handleClosePopup = () => {
    clearPackageDetails();
  };

  const handleSlotSelect = ({ slot, idx }: { slot: SlotTime; idx: number }) => {
    setSessionFromDateSelector({ slot, idx });
  };

  React.useEffect(() => {
    if (packageDetails?.inclusions) {
      setSessionList(
        formattedPackageDetails(
          packageDetails?.inclusions,
          (idx: number) =>
            getDateSelectorContent?.({
              onSelectSlot: ({ slot }) => handleSlotSelect({ slot, idx }),
            }) || null
        )
      );
    } else {
      setSessionList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageDetails?.inclusions]);

  const subText = selectedPatient
    ? `${selectedPatient.fln ?? selectedPatient.fn} | ${selectedPatient.gen}${
        selectedPatient.dob ? ` | ${getAge(new Date(selectedPatient.dob))}` : ""
      }`
    : "";

  const isPackageIdNotPresent = !packageDetails?.package_id;
  const isPricingModelNotPresent = !packageDetails?.pricing_model;
  const isSessionListEmpty = sessionList.length === 0;
  const isPricingModelOneTime =
    PACKAGE_PRICING_MODEL.ONE_TIME === packageDetails?.pricing_model;
  const isTotalPriceNotPresent = isPricingModelOneTime
    ? !packageDetails?.total_price
    : false;
  const isClinicNotSelected = !selectedClinicId;
  const conductingDoctorNotSelected = !conductingDoctor;

  const isDisabled =
    isPackageIdNotPresent ||
    isSessionListEmpty ||
    isTotalPriceNotPresent ||
    isPricingModelNotPresent ||
    isClinicNotSelected ||
    conductingDoctorNotSelected;

  const {
    mutateAsync: postPatientPackageStart,
    isLoading: isPostPatientPackageStartLoading,
  } = usePostPatientPackageStart();

  const handleOkClick = async () => {
    if (
      !packageDetails?.package_id ||
      !selectedClinicId ||
      !selectedPatient ||
      !conductingDoctor
    ) {
      return;
    }
    const packageInclusions: Array<TInclusionPayload> = sessionList.map(
      (pack) => {
        const isMin = pack?.duration
          ? pack.duration?.toLowerCase().includes("min")
          : undefined;
        const val = pack?.duration
          ? Number(pack.duration.split(" ")[0])
          : undefined;
        return {
          name: pack.name,
          description: pack.description,
          duration: isMin ? val : val ? val * 60 : undefined,
          price: pack.price,
          slot:
            pack.startTime && pack.endTime
              ? {
                  clinic_id: selectedClinicId,
                  doctor_oid: conductingDoctor.oid,
                  start_time: pack.startTime,
                  end_time: pack.endTime,
                  mode: MODES_OF_APT.IN_CLINIC,
                  channel: APPOINTMENT_CHANNEL.PACKAGE,
                  action: APPOINTMENT_STATUS.BOOKED,
                  visit_type: TYPES_OF_APT.REGULAR,
                }
              : undefined,
        };
      }
    );

    const response = await postPatientPackageStart({
      data: {
        patient_oid: selectedPatient.oid,
        patient_uuid: selectedPatient.uuid,
        doctor_oid: conductingDoctor.oid,
        clinic_id: selectedClinicId,
        package_payload: {
          max_members: packageDetails.max_members,
          pricing_model: packageDetails.pricing_model,
          description: packageDetails.description,
          package_id: packageDetails.package_id,
          package_name: packageDetails.package_name,
          total_price: packageDetails.total_price,
          inclusions: packageInclusions,
          status: PACKAGE_STATUS.ONGOING,
          expiry:
            packageDetails?.package_expiry_from_start === 0 ||
            !packageDetails.package_expiry_from_start
              ? undefined
              : dayjs()
                  .add(packageDetails.package_expiry_from_start, "days")
                  .format("YYYY-MM-DD"),
        },
      },
    });
    if (response?.success) {
      notification.success({
        message: `${
          packageDetails.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? "Subscription"
            : "Treatment"
        } package for the patient started successfully!`,
        placement: "bottomRight",
      });
      handleClosePopup();
    } else {
      notification.error({
        message: `${
          packageDetails.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? "Subscription"
            : "Treatment"
        } package creation for the patient failed.`,
        placement: "bottomRight",
      });
    }
  };

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={handleClosePopup}
      open={packagePopupType === PACKAGE_POPUP_TYPE.START_TREATMENT}
      width={"90%"}
      extra={null}
      destroyOnClose={true}
      footer={
        <ModalFooter
          isOkButtonDisabled={isDisabled}
          onCancel={handleClosePopup}
          onOk={handleOkClick}
          isSubmitting={isPostPatientPackageStartLoading}
          okText={
            packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
              ? "Start Treatment"
              : "Start Subscription"
          }
        />
      }
    >
      <DrawerHeaderUi
        title={
          packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
            ? "Start Treatment"
            : "Start Subscription"
        }
        subText={subText}
        onClose={handleClosePopup}
      />
      <div className="dr-bg-darwin-neutral-50 dr-p-16 dr-h-full dr-flex dr-flex-col dr-gap-y-24">
        <PackageSelection />
        {packageDetails?.package_id ? (
          <>
            <PackageBreakdown />
            {/* NOTE: It is mandatory to have at least one session associated with a package */}
            <SessionDetails
              pricingModel={packageDetails?.pricing_model}
              disabled={!packageDetails?.staff_modify_access}
            />
          </>
        ) : null}
      </div>
    </Drawer>
  );
};

export default StartTreatmentPopup;

const getDateSelectorContent = ({
  onSelectSlot,
}: {
  onSelectSlot: (props: {
    slot: SlotTime;
    docId: string;
    clinicId: string;
  }) => void;
}) => {
  return <AvailableSlotsList onSlotSelect={onSelectSlot} />;
};
